import { WeValueImagesType, WeValueType } from "../../../models/careers";
import { Box, MainContainer, Block } from "../../../styles/basicStyles";
import { createStyles, getStylesRef } from "@mantine/core";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "next-i18next";
import { IconWrapper, WeValueBg, WeValueCircle1, WeValueCircle2 } from "./styles";
import { Carousel } from "@mantine/carousel";
import { colors } from "../../../styles/colors";
import { IconArrowRight, IconArrowLeft } from '@tabler/icons-react';
import Typography from "../../../components/typography";
import Icon from "../../../components/icon";
import Dot from "../../../components/views/dot";
import Fade from "react-reveal/Fade";
import useLazyBgImage from "../../../hooks/images/useLazyBgImage";

const images: WeValueImagesType[] = [
  {
    _id: 0,
    first_image: "/assets/careers/weValue/img_1.jpg",
  },
  {
    _id: 1,
    first_image: "/assets/careers/weValue/img_2.jpg",
    second_image: "/assets/careers/weValue/img_3.jpg",
  },
  {
    _id: 2,
    first_image: "/assets/careers/weValue/img_4.png",
  },
  {
    _id: 3,
    first_image: "/assets/careers/weValue/img_5.jpg",
    second_image: "/assets/careers/weValue/img_6.jpg",
  },
  {
    _id: 4,
    first_image: "/assets/careers/weValue/img_7.jpg",
    second_image: "/assets/careers/weValue/img_8.jpg",
  },
];

const items: WeValueType[] = [
  {
    _id: 0,
    icon: "outlined_trust",
    title: "CAREERS_WE_VALUE_TITLE_1",
    description: "CAREERS_WE_VALUE_DESCRIPTION_1",
  },
  {
    _id: 1,
    icon: "outlined_heart",
    title: "CAREERS_WE_VALUE_TITLE_2",
    description: "CAREERS_WE_VALUE_DESCRIPTION_2",
  },
  {
    _id: 2,
    icon: "outlined_arrowRight",
    title: "CAREERS_WE_VALUE_TITLE_3",
    description: "CAREERS_WE_VALUE_DESCRIPTION_3",
  },
  {
    _id: 3,
    icon: "outlined_arrowRight",
    title: "CAREERS_WE_VALUE_TITLE_4",
    description: "CAREERS_WE_VALUE_DESCRIPTION_4",
  },
];

const useStyles = createStyles(() => ({
  controls: {
    ref: getStylesRef("controls"),
    transition: "opacity 150ms ease",
    opacity: 0,
  },

  root: {
    "&:hover": {
      [`& .${getStylesRef("controls")}`]: {
        opacity: 1,
      },
    },
  },
}));

const WeValue: FC = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [slideDimensions, setSlideDimensions] = useState({
    slideSize: 400,
    height: 592,
    slideGap: 17,
    withControls: true,
  });
  const imagePaths = images.flatMap((e) => [e.first_image, e.second_image].filter(Boolean) as string[]);
  const bgRefs = useLazyBgImage(imagePaths);

  const handleResize = () => {
    if (window.innerWidth < 992) {
      setSlideDimensions({
        slideSize: 263,
        height: 399,
        slideGap: 11,
        withControls: false,
      });
    } else {
      setSlideDimensions({
        slideSize: 400,
        height: 592,
        slideGap: 17,
        withControls: true,
      });
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Box
      pt={{ md: 4.688, xxs: 4.25 }}
      bgColor={colors.chineseBlack}
      fDirection="column"
      position='relative'
      overflow='hidden'
    >
      <WeValueCircle1 />
      <WeValueCircle2 />
      <Block 
        position='relative'
        zIndex={2}
        w='100%'
      >
        <MainContainer>
          <Typography
            as='h2'
            fSize={{ md: 2.5, xxs: 1.813 }}
            lHeight={{ md: 3, xxs: 2.375 }}
            fWeight={700}
            style={{ color: colors.white }}
          >
            {t("WE_VALUE")}
            <Dot
              w={{ md: 0.875, xxs: 0.5 }}
              h={{ md: 0.875, xxs: 0.5 }}
              ml={0.375}
            />
          </Typography>
          <Box
            mt={{ md: 4.688, xxs: 2.938 }}
            fDirection={{ sm: "row", xxs: "column" }}
            fWrap={{ md: "unset", sm: "wrap", xxs: "unset" }}
          >
            {items.map((e, index) => (
              <Fade bottom key={index}>
                <Box
                  fDirection="column"
                  w={{ md: "auto", sm: "50%", xxs: "unset" }}
                >
                  <Box>
                    <IconWrapper color={colors.lightBlue}>
                      <Box
                        h={2.813}
                        w={2.813}
                        fAlign="center"
                        fJustify="center"
                        zIndex={1}
                      >
                        <Icon icon={e.icon} color={colors.black} size={1.813} />
                      </Box>
                    </IconWrapper>
                    <Typography
                      fSize={{ md: 2, xxs: 1.688 }}
                      lHeight={{ md: 2.375, xxs: 2 }}
                      fWeight={700}
                      style={{ color: colors.white }}
                      ml={{ md: 0.938, xxs: 1.25 }}
                    >
                      {t(e.title)}
                    </Typography>
                  </Box>
                  <Typography
                    fSize={{ md: 1.125, xxs: 1.063 }}
                    lHeight={1.813}
                    fWeight={400}
                    style={{ color: colors.white }}
                    opacity={0.6}
                    mt={{ md: 1.563, xxs: 1 }}
                    mr={{ md: index !== items.length - 1 ? 2.5 : 0, sm: 1, xxs: 0 }}
                    mb={{ md: 0, xxs: index !== items.length - 1 ? 2.25 : 0 }}
                  >
                    {t(e.description)}
                  </Typography>
                </Box>
              </Fade>
            ))}
          </Box>
        </MainContainer>
        <Box mt={{ md: 16.126, xxs: 14.188 }} bgColor={colors.white}>
          <MainContainer>
            <Block mt={{ md: -11.313, xxs: -10 }} mb={{ md: 5.688, xxs: 3.625 }}>
              <Carousel
                maw={1480}
                mx="auto"
                withControls={slideDimensions.withControls}
                slideSize={slideDimensions.slideSize}
                height={slideDimensions.height}
                classNames={classes}
                slidesToScroll={1}
                align="start"
                slideGap={slideDimensions.slideGap}
                loop
                previousControlLabel={t('PREVIOUS_SLIDE')}
                nextControlLabel={t('NEXT_SLIDE')}
              >
                {images.map((e, index) => {
                  const firstImageIndex = imagePaths.indexOf(e.first_image);
                  const secondImageIndex = e.second_image ? imagePaths.indexOf(e.second_image) : null;
              
                  return (
                    !e.second_image ?
                    <Carousel.Slide key={index}>
                      <Box fJustify="center">
                        <WeValueBg 
                          ref={(el: HTMLElement | null) => (bgRefs.current[firstImageIndex] = el)}
                          h={{ md: 37, xxs: 24.938 }}
                          w={{ md: 26, xxs: 16.438 }}
                        />
                      </Box>
                    </Carousel.Slide>
                    :
                    <Carousel.Slide key={index}>
                      <Box fJustify="center" fDirection="column">
                        <WeValueBg 
                          ref={(el: HTMLElement | null) => (bgRefs.current[firstImageIndex] = el)}
                          h={{ md: 18.1, xxs: 12.063 }}
                          w={{ md: 33, xxs: 20.25 }}
                          mb={{ md: 0.875, xxs: 0.688 }}
                        />
                        <WeValueBg 
                          ref={(el: HTMLElement | null) => secondImageIndex && (bgRefs.current[secondImageIndex] = el)}
                          h={{ md: 18.1, xxs: 12.063 }}
                          w={{ md: 33, xxs: 20.25 }}
                        />
                      </Box>
                    </Carousel.Slide>
                  );
                })}
              </Carousel>
            </Block>
          </MainContainer>
        </Box>
      </Block>
    </Box>
  );
};

export default WeValue;
